/**
 * Monitoring Page (engineer and above)
 *
 * Pipeline-health insights:
 * - commit activity: rows inserted + updated per UTC commit hour, per selectable
 *   Hudi table, over a chosen window (default 1 h);
 * - backfill progress per project: completion / in-silver times or estimates,
 *   and records remaining over time.
 */

import React, { useState } from 'react';
import { Card, Stack, Flex, Button, Select, Input, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { useMonitoringTables, useCommitActivity, useBackfillProgress } from '../../api/monitoring';
import { useCallerRole } from '../../auth/useCallerRole';
import { GATES, ROLE_LABELS } from '../../auth/roles';
import { LoadingState } from '../shared/LoadingState';
import { ErrorDisplay } from '../shared/ErrorDisplay';
import {
  backfillCompletion,
  barScale,
  DEFAULT_HOURS,
  formatApiTime,
  WINDOW_OPTIONS,
  windowPhrase,
  type CompletionLine,
} from './monitoringFormat';

const DEFAULT_TABLE = 'silver.consolidated';

/** One-line completion state for a backfill job. `null` when there is nothing to say. */
function completionText(line: CompletionLine): string | null {
  switch (line.kind) {
    case 'in_silver':
      return `Backfill complete at ${formatApiTime(line.completedAt)} · in silver since ${formatApiTime(line.silverAt)}`;
    case 'silver_pending':
      return `Backfill complete at ${formatApiTime(line.completedAt)} · in silver by ${formatApiTime(line.silverAt)}`;
    case 'complete_unknown':
      return 'Backfill complete · completion time unknown';
    case 'estimate':
      return `Backfilling · est. complete ${formatApiTime(line.projectedAt)}` +
        (line.projectedSilverAt ? ` · in silver by ~${formatApiTime(line.projectedSilverAt)}` : '');
    case 'no_estimate':
      return 'Backfilling · estimate unavailable';
    case 'none':
      return null;
  }
}

export function MonitoringPage() {
  const { theme } = useTheme();
  const callerRole = useCallerRole();
  const { data: tables } = useMonitoringTables();
  // Filter INPUTS (what the controls edit) — changing these does not run
  // anything until the user clicks Run.
  const [table, setTable] = useState(DEFAULT_TABLE);
  const [projectInput, setProjectInput] = useState('');
  const [hours, setHours] = useState<number>(DEFAULT_HOURS);

  // COMMITTED query params — only updated on Run. Nothing loads until the user
  // asks: a long window can take ~20 s on a cold read, so filter tweaks and page
  // load never fire a request.
  const [committed, setCommitted] = useState<{
    table: string;
    idProject?: number;
    hours: number;
  } | null>(null);

  const { data: activity, loading, error, refetch } = useCommitActivity(
    committed?.table ?? table,
    committed?.idProject,
    committed?.hours ?? hours,
    committed != null
  );
  const {
    data: backfill,
    loading: backfillLoading,
    error: backfillError,
    refetch: refetchBackfill,
  } = useBackfillProgress(committed?.idProject);

  const idProject = committed?.idProject;

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const borderColor = theme.colors.border.default;
  const primaryColor = theme.colors.primary[600];

  // Whole-screen gate: all three monitoring routes require `engineer`, so there is
  // nothing here a lower role could use. Unlike a gated control, this shows a
  // loading state first — the alternative is flashing access-denied at a user who
  // does have access.
  if (callerRole.isLoading) {
    return <LoadingState message="Checking permissions..." />;
  }

  if (!callerRole.atLeast(GATES.monitoring)) {
    return (
      <Stack gap="lg">
        <h1 style={{ ...textStyles.h3, color: textColor }}>Monitoring</h1>
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" style={{ padding: spacing.lg }}>
              <span style={{ color: theme.colors.semantic.error.default, fontSize: fontSize.sm }}>
                Access denied. {ROLE_LABELS[GATES.monitoring]} role or above required.
              </span>
            </Flex>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  const pendingIdProject = projectInput ? Number(projectInput) : undefined;
  // True when the inputs match what's already committed — Run becomes Refresh.
  const inSync =
    committed != null &&
    committed.table === table &&
    committed.hours === hours &&
    committed.idProject === pendingIdProject;

  const runQuery = () => {
    if (inSync) {
      refetch();
      if (committed.idProject != null) refetchBackfill();
    } else {
      setCommitted({ table, idProject: pendingIdProject, hours });
    }
  };

  const clearQuery = () => {
    setProjectInput('');
    setCommitted(null);
  };

  const buckets = activity?.buckets ?? [];
  const maxCount = buckets.reduce((m, b) => Math.max(m, b.row_count), 0) || 1;
  const totalRows = buckets.reduce((s, b) => s + b.row_count, 0);

  return (
    <Stack gap="lg">
      <div>
        <h1 style={{ ...textStyles.h3, color: textColor }}>Monitoring</h1>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
          Rows inserted or updated by the pipeline per UTC commit hour
          {idProject != null ? ` for id_project ${idProject}` : ' (all projects)'}.
        </p>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.xs, color: mutedColor }}>
          A row written again in a later hour counts in each hour, so these are pipeline activity, not current row totals.
        </p>
      </div>

      {/* Filters — nothing runs until "Run query" is clicked. */}
      <Flex gap="sm" align="center">
        <div style={{ width: 220 }}>
          <Select
            size="sm"
            value={table}
            onChange={setTable}
            options={(tables ?? [{ label: 'Consolidated (silver)', database: 'silver', table: 'consolidated' }]).map((t) => ({
              value: `${t.database}.${t.table}`,
              label: t.label,
            }))}
          />
        </div>
        <div style={{ width: 200 }}>
          <Input
            size="sm"
            type="number"
            placeholder="Filter by id_project…"
            value={projectInput}
            onChange={(e) => setProjectInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') runQuery(); }}
          />
        </div>
        <div style={{ width: 160 }}>
          <Select
            size="sm"
            value={String(hours)}
            onChange={(v) => setHours(Number(v))}
            options={WINDOW_OPTIONS.map((o) => ({ value: String(o.hours), label: o.label }))}
          />
        </div>
        <Button variant="solid" size="sm" onClick={runQuery} disabled={loading}>
          {loading ? 'Running…' : inSync ? 'Refresh' : 'Run query'}
        </Button>
        {committed != null && (
          <Button variant="ghost" size="sm" onClick={clearQuery}>
            Clear
          </Button>
        )}
      </Flex>

      {committed == null ? (
        <Card variant="outlined">
          <Card.Body>
            <Flex direction="column" align="center" gap="sm" style={{ padding: spacing.lg }}>
              <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                Set your filters and click <strong>Run query</strong> to load commit activity.
              </span>
            </Flex>
          </Card.Body>
        </Card>
      ) : loading ? (
        <LoadingState
          message={`Loading commit activity…${
            committed.hours > 24 ? ' The first load of a long window can take up to ~20 seconds.' : ''
          }`}
        />
      ) : error ? (
        <ErrorDisplay error={error} onRetry={refetch} />
      ) : (
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="md">
              <Flex justify="between" align="center">
                <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
                  {table}
                </span>
                <span style={{ color: mutedColor, fontSize: fontSize.xs }}>
                  {buckets.length} commit hours · {totalRows.toLocaleString()} rows
                </span>
              </Flex>

              {buckets.length === 0 ? (
                <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                  No rows written in the {windowPhrase(committed.hours)}.
                </span>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {buckets.map((b) => (
                    <Flex key={b.commit_hour} align="center" gap="sm">
                      <span style={{
                        width: '120px',
                        minWidth: '120px',
                        fontSize: fontSize.xs,
                        fontFamily: 'monospace',
                        color: mutedColor,
                      }}>
                        {b.hour.replace('T', ' ')}
                      </span>
                      <div style={{ flex: 1, background: `${borderColor}40`, borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${Math.max(2, (b.row_count / maxCount) * 100)}%`,
                          background: primaryColor,
                          height: '18px',
                          borderRadius: '3px',
                          transition: 'width 0.2s',
                        }} />
                      </div>
                      <span style={{
                        width: '80px',
                        minWidth: '80px',
                        textAlign: 'right',
                        fontSize: fontSize.xs,
                        color: textColor,
                        fontWeight: 500,
                      }}>
                        {b.row_count.toLocaleString()}
                      </span>
                    </Flex>
                  ))}
                </div>
              )}
            </Stack>
          </Card.Body>
        </Card>
      )}

      {/* Backfill progress (steady-state) — only when an id_project is set */}
      {idProject != null && (
        <Card variant="elevated">
          <Card.Body>
            <Stack gap="md">
              <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'uppercase' }}>
                Backfill Progress — id_project {idProject}
              </span>

              {backfillLoading && !backfill ? (
                <LoadingState message="Loading backfill progress…" />
              ) : backfillError ? (
                <ErrorDisplay error={backfillError} onRetry={refetchBackfill} />
              ) : !backfill || backfill.jobs.length === 0 ? (
                <span style={{ color: mutedColor, fontSize: fontSize.sm }}>
                  No backfill activity recorded for this project.
                </span>
              ) : (
                backfill.jobs.map((job) => {
                  const maxRemaining = barScale(job);
                  const completion = completionText(backfillCompletion(job));
                  const completionColor = job.backfill_complete
                    ? (job.silver_ready ? theme.colors.semantic.success.default : textColor)
                    : mutedColor;
                  return (
                    <div
                      key={`${job.provider}-${job.source_id}`}
                      style={{ border: `1px solid ${borderColor}`, borderRadius: '8px', padding: spacing.md }}
                    >
                      <Flex justify="between" align="center" style={{ marginBottom: spacing.sm }}>
                        <span style={{ fontWeight: 600, color: textColor, fontSize: fontSize.sm, textTransform: 'capitalize' }}>
                          {job.provider} · source {job.source_id}
                        </span>
                        <span style={{
                          fontSize: fontSize.xs,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontWeight: 600,
                          background: job.steady_state
                            ? theme.colors.semantic.success.light + '30'
                            : `${primaryColor}15`,
                          color: job.steady_state ? theme.colors.semantic.success.default : primaryColor,
                        }}>
                          {job.steady_state ? 'Steady state reached' : (job.status || 'running')}
                        </span>
                      </Flex>
                      {completion && (
                        <div style={{ fontSize: fontSize.xs, color: completionColor, fontWeight: 500, marginBottom: spacing.sm }}>
                          {completion}
                        </div>
                      )}
                      <Flex gap="lg" style={{ marginBottom: spacing.sm }}>
                        <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
                          Consumed: <span style={{ color: textColor, fontWeight: 500 }}>{(job.total_consumed ?? 0).toLocaleString()}</span>
                        </span>
                        <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
                          Remaining: <span style={{ color: textColor, fontWeight: 500 }}>{job.records_remaining != null ? job.records_remaining.toLocaleString() : '—'}</span>
                        </span>
                        <span style={{ fontSize: fontSize.xs, color: mutedColor }}>
                          Last update: <span style={{ color: textColor }}>{formatApiTime(job.latest_ts)}</span>
                        </span>
                      </Flex>
                      {/* records_remaining over time */}
                      {job.series.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <span style={{ fontSize: fontSize.xs, color: mutedColor }}>Records remaining over time</span>
                          {job.series.map((p, i) => (
                            <Flex key={`${p.ts}-${i}`} align="center" gap="sm">
                              <span style={{ width: '150px', minWidth: '150px', fontSize: '0.7rem', fontFamily: 'monospace', color: mutedColor }}>
                                {formatApiTime(p.ts)}
                              </span>
                              <div style={{ flex: 1, background: `${borderColor}40`, borderRadius: '3px', overflow: 'hidden' }}>
                                <div style={{
                                  width: `${Math.max(1, ((p.records_remaining ?? 0) / maxRemaining) * 100)}%`,
                                  background: (p.records_remaining ?? 0) === 0 ? theme.colors.semantic.success.default : primaryColor,
                                  height: '14px',
                                  borderRadius: '3px',
                                }} />
                              </div>
                              <span style={{ width: '70px', minWidth: '70px', textAlign: 'right', fontSize: '0.7rem', color: textColor }}>
                                {p.records_remaining != null ? p.records_remaining.toLocaleString() : '—'}
                              </span>
                            </Flex>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </Stack>
          </Card.Body>
        </Card>
      )}
    </Stack>
  );
}
